import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AddLotModal } from '@/components/inventory/AddLotModal'
import { MaterialPickerDialog } from './MaterialPickerDialog'
import type { ApiMaterial } from '@/types/inventory'
import type { StorageCondition } from '@/types/storage'

/**
 * Receive stock from the Storage screens: pick the material, then record what
 * arrived and where it went.
 *
 * The picker itself lives in `MaterialPickerDialog` — putting stock straight
 * into a slot from the map asks the same question.
 */
export function ReceiveStockButton({
  destinationCondition,
}: {
  /**
   * Set where the button is already scoped to one
   * unit — narrows the material list to what THAT unit can keep, the same
   * way `PutAwayPanel` narrows it for one slot (z8nrz7dyd2 AC6). Omitted on
   * the all-units `StorageTab` overview, where no destination is implied and
   * every material is still offered.
   */
  destinationCondition?: StorageCondition | null
} = {}) {
  const [picking, setPicking] = useState(false)

  return (
    <>
      <Button size='sm' variant='outline' className='gap-1.5' onClick={() => setPicking(true)}>
        <Plus className='h-4 w-4' />
        Receive stock
      </Button>
      <ReceiveStockDialogs
        open={picking}
        onClose={() => setPicking(false)}
        destinationCondition={destinationCondition}
      />
    </>
  )
}

/**
 * The receive flow itself, opened by whatever the caller puts in front of it:
 * pick the material, then record the lot. Shared by the button above and the
 * unit page's "Put stock here", so the two cannot drift.
 */
export function ReceiveStockDialogs({
  open,
  onClose,
  destinationCondition,
}: {
  /** Opens the material picker; closing it, or picking, calls `onClose`. */
  open: boolean
  onClose: () => void
  destinationCondition?: StorageCondition | null
}) {
  const [material, setMaterial] = useState<ApiMaterial | null>(null)

  return (
    <>
      <MaterialPickerDialog
        open={open}
        onClose={onClose}
        onPick={(m) => {
          onClose()
          setMaterial(m)
        }}
        destinationCondition={destinationCondition}
      />

      <AddLotModal
        materialId={material?.id ?? null}
        materialLabel={material ? `${material.materialId} · ${material.materialName}` : undefined}
        requiresCondition={material?.requiresCondition ?? null}
        open={material !== null}
        onClose={() => setMaterial(null)}
      />
    </>
  )
}
